<template>
  <div class="main-dashboard">
    <div
      v-for="(item, i) in routes"
      :key="i"
    >
      <f7-list
        accordion-list
        inset
        class="menu-list"
      >
        <f7-list-item
          v-if="item.Procesos.length > 0 || (item.SubModulos && item.SubModulos.length > 0)"
          accordion-item
          :accordion-item-opened="item.Procesos.length > 0 || item.SubModulos.length > 0 ? true : false"
          :title="item.Nombre ?? undefined"
          class="menu-list__item"
        >
          <f7-accordion-content>
            <div v-if="item.Procesos.length > 0">
              <div
                v-for="(pro, j) in item.Procesos"
                :key="j"
                class="menu-list__item__element"
                @click="NavigateRoute(pro.Formulario)"
              >
                <span />
                {{ pro.Nombre }}
              </div>
            </div>
            <!-- <div
              v-else
              class="padding menu-list__item__nodata"
            >
              //<span>No tienes acceso a este módulo, contacta con tu empresa.</span>
            </div> -->
            <f7-list
              v-if="item.SubModulos && item.SubModulos.length > 0"
              accordion-list
              inset
              class="menu-list no-margin-horizontal"
            >
              <f7-list-item
                v-for="(subModulos, k) in item.SubModulos"
                :key="k"
                accordion-item
                :accordion-item-opened="subModulos.Procesos.length > 0 ? true : false"
                :title="subModulos.Nombre ?? undefined"
                class="menu-list__item-submodule"
              >
                <f7-accordion-content>
                  <div>
                    <div
                      v-for="(subModuloSubP, l) in subModulos.Procesos"
                      :key="l"
                      class="menu-list__item__element"
                      @click="NavigateRoute(subModuloSubP.Formulario)"
                    >
                      <span />
                      {{ subModuloSubP.Nombre }}
                    </div>
                  </div>
                </f7-accordion-content>
              </f7-list-item>
            </f7-list>
          </f7-accordion-content>
        </f7-list-item>
      </f7-list>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import {
  APPLICATION_NAME,
  APP_VERSION,
} from '@/js/common/constants';
import { showToast } from '@/js/common/HiNotificationExSupport';
import firebaseAnalytics from '@/js/services/firebase-analytics';
import { callCheckAppUpdate } from '@/js/services/APIErp';
import { f7 } from 'framework7-vue';
import { computed } from 'vue';
import { TOTAL_FORMS } from '@/js/common/developerModeForms';
import { useI18n } from 'vue-i18n';
import { isDeviceCordova } from '@/js/utils/platform';
import { useSettingRef } from '@erpagroapp/settings';

/** @import { Modulo } from '@erpagroapp/types' */

const ENUM_ORIENTATION = Object.freeze({
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
});
const {
  userAccesivility,
} = storeToRefs(useAppConfigStore());

const { t } = useI18n();
const hasInternalConfigurationsAccess = useSettingRef('ConfiguracionesInternas.AccesoHabilitado');
const showAllMenuOptions = useSettingRef('ConfiguracionesInternas.MostrarTodasOpcionesMenu');
const canShowAllMenuOptions = computed(() => hasInternalConfigurationsAccess.value
  && showAllMenuOptions.value);

const routes = computed(() => /** @type {Modulo[]} */ (!canShowAllMenuOptions.value
  ? { ...userAccesivility.value }
  : TOTAL_FORMS));

/**
 * Navigates to a specified route.
 * @param {string | null} route - The route to navigate to.
 * @returns {void}
 */
function NavigateRoute(route) {
  if (!route) {
    return;
  }

  f7.once('routeChanged', handleRouteChanged);

  f7.views.main.router.navigate(route);
  firebaseAnalytics.logPageView(route);

  if (isDeviceCordova()) {
    // @ts-expect-error skip type check, `lock` comes from a cordova plugin
    window.screen.orientation.lock(ENUM_ORIENTATION.PORTRAIT);
  }
}

/**
 * Handles the route change event and checks for a new version of the application.
 * Displays a notification if a new version is available.
 * @throws {Error} If there is an error during the version check.
 * @returns {Promise<void>} A Promise that resolves when the version check is completed.
 */
async function handleRouteChanged() {
  const { data } = await callCheckAppUpdate(APPLICATION_NAME, APP_VERSION);

  if (data.Valor) {
    showToast({
      title: t('appUpdate.common.update'),
      text: t('appUpdate.info.thereIsNewUpdate'),
      horizontalPosition: 'right',
      type: 'warning',
      typeIcon: true,
      closeButton: true,
      closeTimeout: 0,
    });
  }
}
</script>

<style scoped lang="scss">
.main-dashboard {
  --f7-list-item-padding-horizontal: 16px;
  --f7-list-inset-side-margin: 16px;
  --f7-list-margin-vertical: 0;
  --f7-list-item-min-height: 48px;

  .accordion-item-opened > .accordion-item-content {
    border-bottom: unset;
  }

  :deep(.menu-list) {
    --f7-list-bg-color: #fff;

    margin-bottom: 17px;

    .menu-list {
      margin-bottom: 0;
    }

    .menu-list__item {
      --f7-list-item-title-text-color: var(--his-darkgray);
      --f7-list-item-title-font-weight: 600;

      .item-link {
        background-color: #d9d7e0 !important;
        border-bottom: 1px solid var(--his-soft-pearl);
      }

      &.accordion-item-opened {
        .item-link {
          border-radius: var(--f7-list-inset-border-radius) var(--f7-list-inset-border-radius) 0 0 !important;
        }

        > .accordion-item-content {
          padding: 0;
        }
      }

      .accordion-item-content {
        > div:not(.menu-list) {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
          padding: 16px 16px 4px;
        }

        .item-link {
          border-radius: 0 !important;
        }
      }

      &__element {
        border: 1px solid var(--his-ash-gray);
        margin-right: 12px;
        margin-bottom: 12px;
        padding: 10px 18px;
        color: var(--his-darkgray);
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        border-radius: 60px;
        display: flex;
        justify-content: flex-start;
        align-items: baseline;
        background-color: var(--his-soft-pearl);

        span {
          width: 10px;
          height: 10px;
          background-color: var(--his-ajo-dark);
          display: block;
          margin-right: 10px;
        }
      }

      &__nodata {
        color: var(--his-tierra);
        font-size: 14px;
        font-weight: 400;
      }
    }

    .menu-list__item-submodule {
      --f7-list-item-title-text-color: var(--his-darkgray);
      --f7-list-item-title-font-weight: 600;

      .item-link {
        background-color: var(--his-soft-pearl) !important;
        border-bottom: 1px solid var(--his-soft-pearl);
      }

      &.accordion-item-opened {
        .item-link {
          border-radius: var(--f7-list-inset-border-radius) var(--f7-list-inset-border-radius) 0 0 !important;
        }

        > .accordion-item-content {
          padding: 0;
        }
      }

      .accordion-item-content {
        > div:not(.menu-list) {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
          padding: 16px 16px 4px;
        }

        .item-link {
          border-radius: 0 !important;
        }
      }

      &__element {
        border: 1px solid var(--his-ash-gray);
        margin-right: 12px;
        margin-bottom: 12px;
        padding: 10px 18px;
        color: var(--his-darkgray);
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        border-radius: 60px;
        display: flex;
        justify-content: flex-start;
        align-items: baseline;
        background-color: var(--his-soft-pearl);

        span {
          width: 10px;
          height: 10px;
          background-color: var(--his-ajo-dark);
          display: block;
          margin-right: 10px;
        }
      }

      &__nodata {
        color: var(--his-tierra);
        font-size: 14px;
        font-weight: 400;
      }
    }
  }
}
</style>
